import type { FinanceEntrySummary } from '@aeris/contracts'

import {
  dateValue,
  numberValue,
  textValue,
  type ExportColumn,
} from './export/table-export.ts'
import { ORIGIN_LABELS, STATUS_LABELS } from './finance-labels.ts'

/**
 * O que vai no arquivo quando se exporta Financeiro — F158.
 *
 * É o arquivo que o contador pede, então ele leva o que a tela resume: a
 * parcela escrita por extenso, o que já foi baixado, o que falta, e o atraso
 * em dias.
 *
 * **O atraso é do instante em que o servidor respondeu**, não do arquivo:
 * `isOverdue` e `daysOverdue` são derivados na leitura, de propósito
 * (`FinanceEntrySummary`). Uma planilha aberta semana que vem continua dizendo
 * o atraso de hoje — e é por isso que a coluna "Situação" traz "Vencido" junto
 * com o estado, em vez de deixar quem lê deduzir da data.
 */

/**
 * A fatia que esta rota entende — F158.
 *
 * `ExportMenu` conta páginas a partir de 1, como toda lista do produto. O
 * Financeiro é a única rota que pagina por `offset`/`limit`, e escrever
 * `page: exportPage` aqui não daria erro nenhum: a rota ignoraria o campo
 * desconhecido e devolveria a primeira página **vinte vezes**, num arquivo com
 * 2.000 linhas que parece completo e tem 100 registros repetidos.
 *
 * Por isso a conversão tem nome, mora num lugar só e tem teste.
 */
export const financeSlice = (
  page: number,
  pageSize: number
): { readonly offset: number; readonly limit: number } => ({
  offset: (page - 1) * pageSize,
  limit: pageSize,
})

type Column = ExportColumn<FinanceEntrySummary>

/** `2 de 6`, ou vazio quando não é parcelado — repetir "1 de 1" é ruído. */
const instalment = (entry: FinanceEntrySummary): string =>
  entry.instalmentCount <= 1
    ? ''
    : `${String(entry.instalmentNumber)} de ${String(entry.instalmentCount)}`

/**
 * "Vencido há 12 dias" é a resposta à pergunta que se faz olhando a coluna.
 * O estado sozinho ("Em aberto") esconde justamente o que decide a cobrança.
 */
const situation = (entry: FinanceEntrySummary): string =>
  entry.isOverdue
    ? `${STATUS_LABELS[entry.status]} · vencido há ${String(entry.daysOverdue)} ${entry.daysOverdue === 1 ? 'dia' : 'dias'}`
    : STATUS_LABELS[entry.status]

export const FINANCE_EXPORT: readonly Column[] = [
  {
    id: 'documentNumber',
    label: 'Documento',
    value: (entry) => textValue(entry.documentNumber),
  },
  { id: 'partyName', label: 'Pessoa', value: (entry) => entry.partyName },
  {
    id: 'description',
    label: 'Descrição',
    value: (entry) => entry.description,
  },
  {
    id: 'originType',
    label: 'Origem',
    value: (entry) => ORIGIN_LABELS[entry.originType],
  },
  { id: 'instalment', label: 'Parcela', value: instalment },
  { id: 'issuedAt', label: 'Emitido em', value: (e) => dateValue(e.issuedAt) },
  { id: 'dueAt', label: 'Vence em', value: (e) => dateValue(e.dueAt) },
  { id: 'amount', label: 'Valor', value: (e) => numberValue(e.amount) },
  {
    id: 'settledAmount',
    label: 'Baixado',
    value: (e) => numberValue(e.settledAmount),
  },
  {
    id: 'outstanding',
    label: 'Em aberto',
    value: (e) => numberValue(e.outstanding),
  },
  { id: 'status', label: 'Situação', value: situation },
  {
    id: 'daysOverdue',
    label: 'Dias em atraso',
    value: (e) => numberValue(e.isOverdue ? String(e.daysOverdue) : '0'),
  },
  {
    id: 'settledAt',
    label: 'Baixado em',
    value: (e) => dateValue(e.settledAt),
  },
  { id: 'branchName', label: 'Loja', value: (e) => e.branchName },
  { id: 'notes', label: 'Observação', value: (e) => textValue(e.notes) },
  {
    id: 'cancellationReason',
    label: 'Motivo do cancelamento',
    value: (e) => textValue(e.cancellationReason),
  },
]
